import { emojiDefaults } from "@/components/emoji/emoji.type.ts";
import { EmployeeHoverCard } from "@/components/employee/EmployeeHoverCard.tsx";
import { EmployeeItem } from "@/components/employee/EmployeeItem.tsx";
import EmptyStateNothingFound from "@/components/empty-state/EmptyStateNothingFound.tsx";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import { usePerformanceReviewsHook } from "@/hooks/use-performance-reviews.hook.tsx";
import { PerformanceReviewAddFeedbackModal } from "@/pages/talent/performance-reviews/view/widgets/PerformanceReviewAddFeedbackModal.tsx";
import { useEmployeeMe } from "@/queries/employees/use-employees.query.ts";
import { useParticipantSearchInline } from "@/queries/performance-reviews/use-performance-reviews.query.ts";
import { ColumnSizes, Rounding } from "@/types/common.types.ts";
import { getEmployeePositionTitle } from "@/types/employees/employee-position.types.ts";
import { ApiFormParticipantStatus } from "@/types/forms/form.types.ts";
import {
  ApiPerformanceReviewInviteView,
  ApiPerformanceReviewRespondView,
  PerformanceReviewReviewerTypeItems,
} from "@/types/performance-review.types";
import {
  Box,
  Button,
  EmptyStateBase,
  ListItem,
  Skeleton,
  Stack,
  Table,
  Text,
  Title,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  useModals,
} from "@skailer-com/uikit";
import { ColumnDef } from "@tanstack/react-table";
import { round } from "lodash";
import groupBy from "lodash/groupBy";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

export const MyFeedbackRespond = ({
  reviewsRespond,
  reviewsInvite,
  isLoading,
}: {
  reviewsRespond: ApiPerformanceReviewRespondView[];
  reviewsInvite: ApiPerformanceReviewInviteView[];
  isLoading: boolean;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { openModal } = useModals();
  const { getDaysToDate } = useFormatter();
  const { openSelectPeers } = usePerformanceReviewsHook();

  const { data: employee } = useEmployeeMe();
  const { data: participants } = useParticipantSearchInline();

  const handleOpenReviewRespondAction = (review: ApiPerformanceReviewRespondView) => {
    navigate(`/performance-reviews/reviewers/${review.reviewer_id}/respond`, {
      state: { from: location.pathname },
    });
  };

  const reviewsGroupByType = useMemo(() => {
    return groupBy(reviewsRespond, (review) => review.reviewer_type_id);
  }, [reviewsRespond]);

  const columns = useMemo<ColumnDef<ApiPerformanceReviewRespondView>[]>(() => {
    return [
      {
        id: "employees",
        cell: (cell) => (
          <EmployeeHoverCard
            employee={cell.row.original.participant}
            position={getEmployeePositionTitle(cell.row.original.participant)}
          >
            <EmployeeItem showPosition={false} employee={cell.row.original.participant} />
          </EmployeeHoverCard>
        ),
        ...ColumnSizes.standard,
      },
      {
        id: "performance_review",
        cell: (cell) => (
          <Stack>
            <Box className="text-ellipsis overflow-hidden whitespace-nowrap text-dark/95">
              {cell.row.original.title}
            </Box>
            {cell.row.original.comments &&
              cell.row.original.comments.map((comment) => (
                <Box
                  className="text-ellipsis overflow-hidden whitespace-nowrap text-secondary"
                  key={`comment-${comment.id}`}
                >
                  <Tooltip>
                    <TooltipTrigger>
                      <Text>{comment.text}</Text>
                    </TooltipTrigger>
                    <TooltipContent>
                      <Stack gap={"xs"}>
                        <EmployeeItem
                          employee={comment.author}
                          variant={"light"}
                          size={"sm"}
                          showPosition={false}
                        />
                        <Text>{comment.text}</Text>
                      </Stack>
                    </TooltipContent>
                  </Tooltip>
                </Box>
              ))}
          </Stack>
        ),
        ...ColumnSizes.standard,
      },
      {
        id: "deadline",
        cell: (row) => (
          <Box className="flex justify-center w-full text-secondary">
            <Text className={"text-secondary"}>
              {row.row.original.date_ends_at &&
                t("plurals:days_left_short", {
                  count: getDaysToDate(row.row.original.date_ends_at, Rounding.up),
                })}
            </Text>
          </Box>
        ),
        ...ColumnSizes.narrow,
      },
      {
        id: "time_to_complete",
        cell: (row) => (
          <Box className="flex justify-center w-full text-secondary">
            <Text className={"text-secondary"}>
              {"~" +
                t("plurals:plural_minutes", {
                  count: round(row.row.original.form_stats.total_estimated_time_seconds / 60),
                })}
            </Text>
          </Box>
        ),
        ...ColumnSizes.narrow,
      },
      {
        id: "actions",
        cell: (cell) => (
          <Stack className={"justify-end"} gap={"sm"} direction={"horizontal"}>
            <Button
              onClick={() => {
                handleOpenReviewRespondAction(cell.row.original);
              }}
              variant={"secondary"}
            >
              {cell.row.original.reviewer_status == ApiFormParticipantStatus.in_progress
                ? t("performance_review:resume_feedback")
                : t("common:leave_feedback")}
            </Button>
          </Stack>
        ),
        ...ColumnSizes.standard,
      },
    ];
  }, [reviewsRespond]);

  return (
    <Box>
      <Stack gap={"xl"} className={"my-8"}>
        {isLoading && <Skeleton className={"w-auto h-20"} />}

        <Stack gap={"smd"}>
          {participants?.items && participants?.items.length > 0 && (
            <Box className={"rounded-xs border-brand-dark/5 px-6 py-4 bg-dark/5 border-1"}>
              <ListItem
                emoji={":writing_hand:"}
                preTitle={t("performance_review:additional_feedback_pre_title")}
                title={t("performance_review:additional_feedback_title")}
                emphasis="top"
                behavior="card"
                valueSlot={
                  <Stack direction={"horizontal"} gap={"sm"}>
                    <Button
                      variant={"primary"}
                      onClick={() => {
                        openModal(PerformanceReviewAddFeedbackModal);
                      }}
                    >
                      {t("performance_review:additional_feedback_choose_colleague")}
                    </Button>
                  </Stack>
                }
              />
            </Box>
          )}
          {reviewsInvite?.map((item) => (
            <Box
              key={`item-invite-${item.review.id}`}
              className={"rounded-xs border-brand-dark/5 px-6 py-4 bg-dark/5 border-1"}
            >
              <ListItem
                emoji={item.has_invitations_from_user ? ":man-tipping-hand:" : ":man-raising-hand:"}
                preTitle={
                  item.has_invitations_from_user
                    ? t("performance_review:app_notification_invite_peers_pre_title_selected", {
                        title: item.review.title,
                      })
                    : t("performance_review:app_notification_invite_peers_pre_title")
                }
                title={
                  item.has_invitations_from_user
                    ? t("performance_review:app_notification_invite_peers_title_selected")
                    : `${t("performance_review:app_notification_invite_peers_title")} "${item.review.title}"`
                }
                emphasis="top"
                behavior="card"
                valueSlot={
                  <Stack direction={"horizontal"} gap={"sm"}>
                    <Button
                      variant={"primary"}
                      onClick={() => {
                        if (!employee) return;
                        openSelectPeers({
                          performanceReviewId: item.review.id,
                          employeeId: employee.id,
                          reviewerTypes: item.reviewer_types,
                          withStatuses: false,
                        });
                      }}
                    >
                      {item.has_invitations_from_user
                        ? t("performance_review:edit_peers")
                        : t("performance_review:select_peers")}
                    </Button>
                  </Stack>
                }
              />
            </Box>
          ))}
        </Stack>

        {reviewsRespond.length > 0 ? (
          Object.entries(reviewsGroupByType).map(([reviewerTypeId, items]) => {
            const [reviewerType] = items;
            return (
              <Box key={reviewerTypeId}>
                <Title
                  header={
                    PerformanceReviewReviewerTypeItems().find(
                      (x) => x.id == reviewerType.reviewer_type
                    )?.title ??
                    reviewerType.reviewer_type_title ??
                    t("common:no_title")
                  }
                  paddingBottom
                />
                <Table
                  layout="fixed"
                  stickyLastColumn={true}
                  data={items}
                  columns={columns}
                  notFoundComponent={<EmptyStateNothingFound />}
                  hideHeader={true}
                />
              </Box>
            );
          })
        ) : (
          <EmptyStateBase
            emoji={emojiDefaults.default}
            title={t("performance_review:my_reviews_empty_title")}
            description={t("performance_review:my_reviews_empty_description")}
          />
        )}
      </Stack>
    </Box>
  );
};
